import { useEffect, useRef } from 'react';

/**
 * Componente aislado para Cloudflare Turnstile (S10-10).
 *
 * Aislado a propósito en `lib/captcha/` para portabilidad: si en el
 * futuro se migra a otro captcha (hCaptcha self-host bajo Contabo,
 * por ejemplo), solo este archivo cambia. La interfaz `TurnstileWidgetProps`
 * sirve de contrato — un reemplazo debe entregar `onToken(string | null)`
 * con el mismo timing.
 */

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

interface TurnstileApi {
  render: (
    el: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    },
  ) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

function ensureScript() {
  if (document.querySelector(`script[src="${TURNSTILE_SCRIPT_URL}"]`)) return;
  const s = document.createElement('script');
  s.src = TURNSTILE_SCRIPT_URL;
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}

export interface TurnstileWidgetProps {
  siteKey: string;
  onToken: (token: string | null) => void;
}

export function TurnstileWidget({ siteKey, onToken }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);

  // Mantenemos la referencia del callback fresca sin disparar re-render
  // del widget.
  useEffect(() => {
    onTokenRef.current = onToken;
  }, [onToken]);

  useEffect(() => {
    ensureScript();
    let cancelado = false;
    let intentos = 0;

    function tryRender() {
      if (cancelado) return;
      if (!window.turnstile || !containerRef.current) {
        intentos += 1;
        if (intentos > 100) return;
        setTimeout(tryRender, 100);
        return;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => onTokenRef.current(token),
        'expired-callback': () => onTokenRef.current(null),
        'error-callback': () => onTokenRef.current(null),
      });
    }
    tryRender();

    return () => {
      cancelado = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Ignorar errores de remove al desmontar — el widget puede
          // ya no existir si el script falló al cargar.
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  return <div ref={containerRef} />;
}
